import { Input, Modal, Select, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import axiosConfig, { fetcher, handleMutation } from "../util/axios";
import requestErrorHandler from "../util/requestErrorHandler";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import useSWRMutation from "swr/mutation";
import useSWR from "swr";

type Props = {
  open: boolean;
  user: User | null;
  onCancel: () => void;
};

type Bank = {
  name: string;
  code: string;
}

export default function AddSettlementAccountModal({
  open,
  user,
  onCancel,
}: Props) {
  // const [filteredBanks, setFilteredBanks] = useState<Bank[]>([]);
  const [formData, setFormData] = useState<{
    accountNumber: string;
    bankCode: string;
    accountName: string;
  }>({
    bankCode: "",
    accountNumber: "",
    accountName: ""
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [lastResolveAttempt, setLastResolveAttempt] = useState<string>("");

  const { data, isLoading: isLoadingBanks } = useSWR(
    { url: "payments/banks" },
    fetcher,
  );

  const allBanks: Bank[] = useMemo(() => {
    return data?.data || [];
  }, [data])

  const { trigger: resolveAccount, isMutating: isResolving } = useSWRMutation(
    "payments/resolve-account",
    handleMutation,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResolve = useCallback(async (accountNumber: string, bankCode: string) => {
    try {
      const resp = await resolveAccount({accountNumber, bankCode});
      setFormData(prev => ({...prev, accountName: resp?.data?.accountName}))
      setLastResolveAttempt(`${accountNumber}-${bankCode}`); // Mark as successful
    } catch (error) {
      console.error(error);
      requestErrorHandler(error);
      // Don't update lastResolveAttempt on failure to allow manual retry
    }
  }, [resolveAccount]);

  useEffect(() => {
    const { accountNumber, bankCode } = formData;
    const attemptKey = `${accountNumber}-${bankCode}`;
    
    // Only resolve if conditions met, haven't already attempted this combo, and no accountName yet
    if (
      accountNumber.length > 0 && 
      accountNumber.length > 9 && 
      bankCode && 
      lastResolveAttempt !== attemptKey &&
      !formData.accountName
    ) {
      void handleResolve(accountNumber, bankCode);
    }
  }, [formData, lastResolveAttempt, handleResolve]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axiosConfig.post("/fleet/settlement-accounts", {
        ...formData,
        bankName: allBanks.find((bank: Bank) => bank.code === formData.bankCode)?.name || '',
      });
      message.success("Settlement account created");
      setFormData({
        accountName: '',
        accountNumber: '',
        bankCode: ''
      })
      onCancel()
    } catch (error) {
      requestErrorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      footer={null}
      open={open}
      title="Add New Settlement Account"
      onCancel={onCancel}
    >
    <div>
      <form
        onSubmit={handleSubmit}
        className={cn("space-y-8", isSubmitting ? "opacity-50" : "opacity-100")}
      >
        <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <label htmlFor="bank" className="block font-medium">
                Bank Name
              </label>
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder="Enter your bank name"
                loading={isLoadingBanks}
                value={formData.bankCode}
                style={{ width: '100%' }}
                options={allBanks.map((d: Bank) => ({
                  value: d.code,
                  label: d.name,
                }))}
                onChange={(val) => setFormData({...formData, bankCode: val})}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="accountNumber" className="block font-medium">
                Account Number*
              </label>
              <Input
                id="accountNumber"
                name="accountNumber"
                type="number"
                placeholder="Enter Account Number"
                value={formData.accountNumber}
                onChange={handleChange}
                required
              />
              {formData.accountName && (
                <span className="block text-sm text-primary font-medium text-right">
                  {formData.accountName}
                </span>
              )}
            </div>
        </div>
        <div className="flex justify-center">
          <Button
            disabled={isSubmitting || !formData.accountName}
            type="submit"
            className="text-lg w-full md:w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-2"
          >
            {isSubmitting || isResolving
            ? (<Loader2 className="animate-spin h-5 w-5" />)
            : "Save"}
          </Button>
        </div>
      </form>
    </div>
    </Modal>
  );
}